import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, ChevronLeft, ChevronRight, MessageCircle, Send, Trash2, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/i18n";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const REACTIONS = [
  { key: "like", icon: "👍" },
  { key: "love", icon: "❤️" },
  { key: "party", icon: "🎉" },
  { key: "clap", icon: "👏" },
  { key: "wow", icon: "😮" },
];

/** Photos of the week: masonry grid + lightbox, with emoji reactions. */
export const PhotosOfWeek = ({ limit = 4, title = null, showGalleryLink = true, groupByWeek = false, manage = false }) => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(null);
  const [comments, setComments] = useState({});
  const [draft, setDraft] = useState("");
  // Una foto que no carga no se muestra: antes dejaba el ícono roto en el panel.
  const [rotas, setRotas] = useState([]);
  const isAcademic = ["superadmin", "academic_director"].includes(user?.role);

  const openPhoto = async (i) => {
    setIdx(i);
    setDraft("");
    const p = photos[i];
    if (p && !comments[p.id]) {
      const r = await api.get(`/wall/photos/${p.id}/comments`);
      setComments((c) => ({ ...c, [p.id]: r.data || [] }));
    }
  };

  const sendComment = async (p) => {
    const text = draft.trim();
    if (!text) return;
    const r = await api.post(`/wall/photos/${p.id}/comments`, { text });
    setComments((c) => ({ ...c, [p.id]: [...(c[p.id] || []), r.data] }));
    setDraft("");
    setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, comment_count: (x.comment_count || 0) + 1 } : x)));
  };

  const removeComment = async (p, c) => {
    await api.delete(`/wall/comments/${c.id}`);
    setComments((s) => ({ ...s, [p.id]: (s[p.id] || []).filter((x) => x.id !== c.id) }));
    setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, comment_count: Math.max(0, (x.comment_count || 1) - 1) } : x)));
  };

  useEffect(() => {
    api.get("/wall/photos").then((r) => setPhotos(r.data || [])).catch(() => {});
  }, []);

  const react = async (photo, emoji) => {
    const before = photo.my_reaction;
    setPhotos((prev) => prev.map((p) => {
      if (p.id !== photo.id) return p;
      const rx = { ...(p.reactions || {}) };
      if (before) rx[before] = { ...rx[before], count: Math.max(0, (rx[before]?.count || 1) - 1) };
      const next = before === emoji ? null : emoji;
      if (next) rx[next] = { ...rx[next], count: (rx[next]?.count || 0) + 1 };
      return { ...p, my_reaction: next, reactions: rx };
    }));
    try { await api.post(`/wall/photos/${photo.id}/react`, { emoji }); } catch { /* keep optimistic */ }
  };

  const reactionRow = (p, size = "sm") => (
    <div className={`flex flex-wrap items-center gap-1 ${size === "lg" ? "justify-center" : ""}`} data-testid={`photo-reactions-${p.id}`}>
      {REACTIONS.map((r) => {
        const count = p.reactions?.[r.key]?.count || 0;
        const mine = p.my_reaction === r.key;
        return (
          <button
            key={r.key}
            type="button"
            onClick={(e) => { e.stopPropagation(); react(p, r.key); }}
            title={(p.reactions?.[r.key]?.names || []).join(", ")}
            className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${mine ? "border-brand-blue bg-blue-50 text-brand-blue font-semibold" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
            data-testid={`photo-react-${p.id}-${r.key}`}
          >
            <span className="text-xs leading-none">{r.icon}</span>
            {count > 0 && <span data-testid={`photo-react-count-${p.id}-${r.key}`}>{count}</span>}
          </button>
        );
      })}
    </div>
  );

  const weekLabel = (iso) => {
    const d = new Date(iso);
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (x) => x.toLocaleDateString(lang === "en" ? "en-US" : "es-CR", { day: "numeric", month: "short" });
    return t("photo_week_label", { from: fmt(monday), to: fmt(sunday) });
  };

  const canManage = (p) => manage && (isAcademic || p.uploaded_by === user?.user_id);

  const editCaption = async (p) => {
    const next = window.prompt(t("photo_caption_prompt"), p.caption || "");
    if (next === null) return;
    await api.patch(`/wall/photos/${p.id}`, { caption: next });
    setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, caption: next } : x)));
    toast.success(t("photo_caption_saved"));
  };

  const removePhoto = async (p) => {
    if (!window.confirm(t("photo_delete_confirm"))) return;
    await api.delete(`/wall/photos/${p.id}`);
    setPhotos((prev) => prev.filter((x) => x.id !== p.id));
  };

  const tile = (p, i) => (
    <div key={p.id} className="space-y-1.5 relative" data-testid={`photo-tile-${p.id}`}>
      {canManage(p) && (
        <div className="absolute top-1 right-1 z-10 flex gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7 bg-white/90 hover:bg-white" onClick={() => editCaption(p)} data-testid={`gallery-edit-photo-${p.id}`}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 bg-white/90 text-red-600 hover:bg-white" onClick={() => removePhoto(p)} data-testid={`gallery-delete-photo-${p.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
      <button
        type="button"
        onClick={() => openPhoto(i)}
        className="group relative block w-full aspect-square overflow-hidden rounded-xl"
        data-testid={`photo-${p.id}`}
      >
        <img
          src={`${API_BASE}/api/wall/photos/${p.id}/image`}
          alt={p.caption || "Foto de la semana"}
          onError={() => setRotas((r) => (r.includes(p.id) ? r : [...r, p.id]))}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {p.caption && (
          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pt-6 pb-2 text-left text-[11px] font-medium text-white line-clamp-2">
            {p.caption}
          </span>
        )}
      </button>
      {reactionRow(p)}
    </div>
  );

  const sanas = photos.filter((p) => !rotas.includes(p.id));
  if (sanas.length === 0) return null;
  const shown = sanas.slice(0, limit);
  const current = idx !== null ? photos[idx] : null;

  return (
    <section data-testid="photos-of-week">
      <div className="flex items-center gap-2 mb-3">
        <Camera className="h-4 w-4 text-orange-500" />
        <h2 className="text-base font-bold text-slate-900">{title || t("sa_photos_title")}</h2>
        {showGalleryLink && sanas.length > limit && (
          <Link to="/dashboard/gallery" className="ml-auto text-xs font-semibold text-brand-blue hover:underline" data-testid="see-gallery-link">
            {t("see_full_gallery", { n: sanas.length })}
          </Link>
        )}
      </div>

      {groupByWeek ? (
        <div className="space-y-8">
          {[...shown.reduce((map, p) => {
            const k = weekLabel(p.created_at);
            map.set(k, [...(map.get(k) || []), p]);
            return map;
          }, new Map())].map(([label, group]) => (
            <div key={label} data-testid={`photo-week-${label}`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.map((p) => tile(p, photos.indexOf(p)))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {shown.map((p) => tile(p, photos.indexOf(p)))}
        </div>
      )}

      <Dialog open={idx !== null} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent className="max-w-5xl bg-slate-950 border-slate-800 p-2" data-testid="photo-lightbox" aria-describedby={undefined}>
          {current && (
            <div className="relative">
              <img
                src={`${API_BASE}/api/wall/photos/${current.id}/image`}
                alt={current.caption || ""}
                className="w-full max-h-[72vh] object-contain rounded-lg"
              />
              <div className="flex items-center justify-between gap-3 px-2 py-2">
                <Button size="icon" variant="ghost" className="text-white hover:bg-white/10"
                  onClick={() => openPhoto((idx - 1 + photos.length) % photos.length)} data-testid="photo-prev">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <p className="text-sm text-white/90 text-center flex-1">{current.caption || ""}</p>
                <Button size="icon" variant="ghost" className="text-white hover:bg-white/10"
                  onClick={() => openPhoto((idx + 1) % photos.length)} data-testid="photo-next">
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="pb-2">{reactionRow(current, "lg")}</div>
              <div className="border-t border-slate-800 pt-3 px-2 pb-2 space-y-2 max-h-52 overflow-y-auto" data-testid={`photo-comments-${current.id}`}>
                <p className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/60 font-semibold">
                  <MessageCircle className="h-3 w-3" /> {current.comment_count || 0} comentarios
                </p>
                {(comments[current.id] || []).map((c) => (
                  <div key={c.id} className="flex items-start gap-2" data-testid={`photo-comment-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-white/80">{c.user_name}</p>
                      <p className="text-sm text-white/90">{c.text}</p>
                    </div>
                    {(isAcademic || c.user_id === user?.user_id) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:bg-white/10"
                        onClick={() => removeComment(current, c)} data-testid={`photo-comment-delete-${c.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <Input
                    className="bg-slate-900 border-slate-700 text-white placeholder:text-white/40"
                    placeholder={t("write_comment_ph")}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendComment(current)}
                    data-testid={`photo-comment-input-${current.id}`}
                  />
                  <Button size="icon" onClick={() => sendComment(current)} data-testid={`photo-comment-send-${current.id}`}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PhotosOfWeek;
